import { NgModule, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeDa from '@angular/common/locales/da';
import localeDaExtra from '@angular/common/locales/extra/da';
import { httpInterceptorProviders } from './core/http-interceptors';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { AuthModule } from './auth/auth.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { NotificationsModule } from './notifications/notifications.module';
import { TimetrackModule } from './timetrack/timetrack.module';
import { AppStoreModule } from './store/app-store.module';
import { TasksModule } from './tasks/tasks.module';
import { HolidayModule } from './holiday/holiday.module';
import { InvoicesModule } from './invoices/invoices.module';
import { StatistikModule } from './statistik/statistik.module';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard, MsalGuardConfiguration,
    MsalInterceptor, MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';

registerLocaleData(localeDa, 'da', localeDaExtra);

const isCloudMode = environment['authMode'] === 'cloud';

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment['msalConfig']?.['auth']?.['clientId'],
            authority: environment['msalConfig']?.['auth']?.['authority'],
            navigateToLoginRequestUrl: false,
            postLogoutRedirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
        },
        system: {
            allowPlatformBroker: false,
        },
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(
        environment['apiConfig']?.['uri'],
        environment['apiConfig']?.['scopes'] || []
    );

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    console.log('MSAL Guard Config Factory', environment['apiConfig']?.['uri'], environment['apiConfig']?.['scopes'] || []);
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment['apiConfig']?.['scopes'] || []]
        },
        loginFailedRoute: '/login-failed',
    };
}

export function initializeMsalInstance(msalInstance: IPublicClientApplication): () => Promise<void> {
    return () => msalInstance.initialize();
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        AppStoreModule,
        BrowserAnimationsModule,
        CoreModule,
        AuthModule,
        DashboardModule,
        NotificationsModule,
        TimetrackModule,
        TasksModule,
        HolidayModule,
        InvoicesModule,
        StatistikModule
    ],
    providers: [
        {provide: LOCALE_ID, useValue: 'da'},
        httpInterceptorProviders,
        provideHttpClient(withInterceptorsFromDi()),
        // MSAL providers - kun hvis authMode er 'cloud'
        ...(isCloudMode ? [
            {
                provide: APP_INITIALIZER,
                useFactory: initializeMsalInstance,
                multi: true,
                deps: [MSAL_INSTANCE]
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: MsalInterceptor,
                multi: true,
            },
            {
                provide: MSAL_INSTANCE,
                useFactory: MSALInstanceFactory,
            },
            {
                provide: MSAL_GUARD_CONFIG,
                useFactory: MSALGuardConfigFactory,
            },
            {
                provide: MSAL_INTERCEPTOR_CONFIG,
                useFactory: MSALInterceptorConfigFactory,
            },
            MsalService,
            MsalGuard,
            MsalBroadcastService,
        ] : [])
    ]
})
export class AppModule {
}
